import React, { useEffect, useState } from 'react';
import moment, { Moment } from 'moment';
import Calendar, { CalendarResource, CalendarSettings } from './Calendar';
import CalendarPatientEventModal from './CalendarPatientEventModal';
import apiClient from '../../Services/ApiClient';
import { workplaceWorkingHours } from '../../Modules/Workplace/Services/workplace-working-hours.service';
import { useEffectAsync } from '../../helpers';
import { WORKPLACES } from '../../Modules/Workplace/Graphql/workplaces.graphql';
import styles from './AllWorkplacesCalendar.module.scss';
import { useAppContext } from '../../Services/AppContext';
import CalendarContextMenu from './CalendarContextMenu';
import { getMomentDate } from '../../Utils/dates.util';
import useServiceSchedules from '../../Modules/Workplace/Hooks/useServiceSchedules';
import {
	EventType,
	ServiceScheduleType,
	useLazyWorkplaceEventsQuery,
} from '../../graphql/graphql';
import AllWorkplacesCalendarFilter, {
	AllWorkplacesCalendarFilterValues,
} from '../../Modules/Calendar/Components/AllWorkplacesCalendarFilter/AllWorkplacesCalendarFilter';
import useLoading from '../../Modules/Base/Hooks/useLoading';
import OverlayLoading from '../OverlayLoading';
import CalendarSettingsModal from '../../Modules/Calendar/Components/CalendarSettings/CalendarSettingsModal';
import { CustomSettingKey } from '../../Modules/Base/Enums/CustomSettingKey.enum';
import { formatHour } from '../../Modules/Calendar/Helpers/formatSlotHour.helper';
import useCustomSettings from '../../Modules/Base/Hooks/useCustomSettings';
import { ContextMenuParams } from '../ContextMenu/ContextMenu';
import {
	BusinessHoursInput,
	EventApi,
	EventClickArg,
	EventInput,
} from '@fullcalendar/react';
import useApiClient from '../../Modules/Base/Hooks/useApiClient';
import { Theme } from '../../Enums/Theme.enum';
import LinkButton from '../Button/LinkButton';
import { DEFAULT_SLOT_DURATION_OPTIONS } from '../../Modules/Manager/Components/ManagerWrapper/Calendar/ManagerCalendarViewSettings/ManagerCalendarViewSettings';
import { SelectValue } from '../Base/Select/Select';
import { CalendarView } from '../../Modules/Calendar/Enums/CalendarView.eunm';

interface AllWorkplacesCalendarProps {
	patientId?: string;
}

interface WorkplaceItem {
	id: string;
	value: string;
	title: string;
	label: string;
	businessHours: BusinessHoursInput[];
}

const DEFAULT_VIEW_OPTIONS: SelectValue[] = [
	{
		label: 'component.managerCalendarViewSettings.defaultViewOptions.day',
		value: CalendarView.RESOURCE_TIME_GRID_DAY,
	},
	{
		label: 'component.managerCalendarViewSettings.defaultViewOptions.week',
		value: CalendarView.RESOURCE_TIME_GRID_WEEK,
	},
];

export default function AllWorkplacesCalendar(
	props: AllWorkplacesCalendarProps,
) {
	const { patientId } = props;
	const { workplaceId, userData } = useAppContext();
	const { displayError } = useApiClient();
	const [getWorkplaceEvents] = useLazyWorkplaceEventsQuery();
	const { getWorkplaceSchedules } = useServiceSchedules();
	const [detailOpened, setDetailOpened] = useState(false);
	const [selectedDate, setSelectedDate] = useState<Moment | null>(null);
	const [selectedEvent, setSelectedEvent] = useState<EventClickArg | undefined>(
		undefined,
	);
	const [dateRange, setDateRange] = useState<
		{ start: Moment; end: Moment } | undefined
	>(undefined);
	const [events, setEvents] = useState<EventInput[]>([]);
	const [selectedWorkplaceId, setSelectedWorkplaceId] = useState<
		string | undefined
	>(undefined);
	const [loadingEvents, setLoadingEvents] = useState(false);
	const [resources, setResources] = useState<CalendarResource[]>([]);
	const [workplaces, setWorkplaces] = useState<WorkplaceItem[]>([]);
	const [contextMenuProps, setContextMenuProps] = useState<ContextMenuParams>({
		isOpen: false,
	});
	const [filter, setFilter] = useState<
		AllWorkplacesCalendarFilterValues | undefined
	>();
	const { isLoading, setIsLoading } = useLoading(false);
	const [isCalendarSettingOpen, setIsCalendarSettingOpen] = useState(false);
	const { getCustomSettings } = useCustomSettings(workplaceId, userData.id);
	const [settings, setSettings] = useState<CalendarSettings | undefined>(
		undefined,
	);
	const {
		isLoading: isCalendarSettingsLoading,
		setIsLoading: setIsCalendarSettingsLoading,
		RenderLoading,
	} = useLoading(false);

	useEffectAsync(async () => {
		await handleLoadResources();
		setIsLoading(false);
	}, []);

	useEffect(() => {
		handleSetResources();
	}, [workplaces, filter]);

	useEffectAsync(async () => {
		setLoadingEvents(true);
		await handleLoadData();
	}, [dateRange, resources]);

	useEffect(() => {
		setLoadingEvents(false);
	}, [events]);

	const handleSetResources = () => {
		const tWorkplacesIds = filter?.workplaceIds?.length
			? filter.workplaceIds
			: [workplaceId];
		setResources(workplaces.filter(w => tWorkplacesIds.indexOf(w.id) !== -1));
	};

	const handlePickDate = (date: Moment, resourceId?: string) => {
		setSelectedDate(date);
		setSelectedWorkplaceId(resourceId);
		setDetailOpened(true);
	};

	const handleEventClick = (event: EventClickArg) => {
		setSelectedEvent(event);
		setContextMenuProps({
			isOpen: true,
			clientX: event.jsEvent.clientX,
			clientY: event.jsEvent.clientY,
		});
	};

	const handleEditEvent = () => {
		setSelectedWorkplaceId(selectedEvent?.event?.extendedProps.workplaceId);
		setDetailOpened(true);
		setContextMenuProps({ isOpen: false });
	};

	const handleDateChange = (start: Moment, end: Moment) => {
		setDateRange({ start, end });
	};

	const handleClose = async () => {
		setSelectedEvent(undefined);
		setDetailOpened(false);
		await handleLoadData();
	};

	const handleRemoveEvent = async (event: EventClickArg) => {
		try {
			await apiClient.delete('/events/cancel/' + event.event.id);
		} catch (e) {
			displayError(e);
		}
		setContextMenuProps({ isOpen: false });
		await handleLoadData();
	};

	const handleLoadData = async () => {
		if (!dateRange || !resources.length) {
			setEvents([]);
			return;
		}

		const a = async () => {
			if (!dateRange) return;

			const tempEvents: EventInput[] = [];

			const variables = {
				start: dateRange.start.format(),
				end: (dateRange?.end || dateRange.start).format(),
				types: [EventType.Default, EventType.Reservation],
				workplaceIds: resources.map(r => r.id),
			};

			const workplaceEvents = await getWorkplaceEvents(variables);

			for (const event of workplaceEvents?.data?.workplaceEvents?.data || []) {
				let isChild = false;
				let patientsName;
				let patientPhone;
				let patientBirthNumber;
				let patientAge;
				let patientId;
				const doctor: { id?: string; name?: string } = {};
				event?.eventMembers?.forEach((user, index) => {
					if (user?.user?.isPatient) {
						patientId = user?.user?.id;
						patientsName = user.user?.profile?.fullName;
						patientAge = user.user?.profile?.age;
						patientBirthNumber = user?.user?.profile?.birthNumber;
						patientPhone = user?.user?.formattedPhoneNumber;
					}
					if (user?.user?.isChild) {
						patientId = user?.user?.id;
						patientsName = user?.user?.profile?.fullName;
						patientAge = user?.user?.profile?.age;
						patientBirthNumber = user?.user?.profile?.birthNumber;
						patientPhone = user?.user?.formattedPhoneNumber;
						isChild = true;
					}
					if (user?.user?.isDoctor) {
						if (!doctor.name) {
							doctor.name = user.user?.profile?.fullName;
							doctor.id = user.user?.id;
						}
					}
				});
				tempEvents.push({
					id: event.id,
					title: event.title,
					doctor,
					patient: {
						id: patientId ? patientId : '',
						name: patientsName ? patientsName : '',
						age: patientAge ? patientAge : '',
						birthNumber: patientBirthNumber ? patientBirthNumber : '',
						phone: patientPhone ? patientPhone : '',
						isChild,
					},
					workplace: event.calendar?.workplace.doctorDisplayName ?? null,
					workplaceId: event.calendar?.workplaceId ?? null,
					start: moment(event.dateFrom).format(),
					end: moment(event.dateTo).format(),
					backgroundColor: event.layout.backgroundColor,
					borderColor: event.layout.backgroundColor,
					textColor: event.layout.fontColor,
					resourceId: event.calendar?.workplaceId ?? undefined,
					note: event.description,
					meta: event.meta,
				});
			}

			const tempEvents2 = await getWorkplaceSchedules({
				from: getMomentDate(dateRange.start),
				to: getMomentDate(dateRange.end || dateRange.start),
				workplaceIds: resources.map(r => r.id),
				type: ServiceScheduleType.Shift,
				allDay: true,
			});
			tempEvents2.map(t => tempEvents.push(t));

			const tempEvents3 = await getWorkplaceSchedules({
				from: getMomentDate(dateRange.start),
				to: getMomentDate(dateRange.end || dateRange.start),
				workplaceIds: resources.map(r => r.id),
				type: ServiceScheduleType.ShiftExtra,
				allDay: true,
			});
			tempEvents3.map(t => tempEvents.push(t));
			setEvents(tempEvents);
		};

		await a();
	};

	const handleEventDrop = async (event: EventApi) => {
		let workplaceId: string | undefined = undefined;

		for (const resource of event.getResources()) {
			workplaceId = resource.id;
		}

		const a = async () => {
			await apiClient.patch(`/event/${event.id}`, {
				dateFrom: moment(event.start).format(),
				dateTo: moment(event.end).format(),
				propIgnoreFreeTimeCheck: true,
				workplaceId,
			});
		};

		await a();
	};

	const handleLoadResources = async () => {
		const tempWorkplaces = [];

		const { data } = await apiClient.graphql({
			query: WORKPLACES,
			variables: {
				type: 'normal',
			},
			fetchPolicy: 'network-only',
		});

		for (const workplace of data.workplaces.data || []) {
			const item: WorkplaceItem = {
				id: workplace.id,
				value: workplace.id,
				title: workplace.doctorDisplayName,
				label: workplace.doctorDisplayName,
				businessHours: [],
			};

			item.businessHours = await workplaceWorkingHours({
				workplaceId: workplace.id,
			});

			tempWorkplaces.push(item);
		}

		setWorkplaces(tempWorkplaces);
	};

	const handleCreateNewEvent = (date: Moment) => {
		setSelectedEvent(undefined);
		handlePickDate(getMomentDate(date));
	};

	const handleAfterMarkDone = async () => {
		setSelectedEvent(undefined);
		setContextMenuProps({ isOpen: false });
		await handleLoadData();
	};

	const handleOnFilter = (values: AllWorkplacesCalendarFilterValues) => {
		setIsLoading(true);
		setFilter(values);
		setIsLoading(false);
	};

	const handleLoadCustomSettings = async () => {
		setIsCalendarSettingsLoading(true);
		const settings = await getCustomSettings(
			CustomSettingKey.ALL_WORKPLACES_CALENDAR_SETTINGS,
		);

		if (settings) {
			setSettings({
				showWeekends: settings.showWeekends,
				highlightWorkingTime: settings.highlightWorkingTime,
				slotMinTime: formatHour(settings.calendarViewTime?.showCalendarFrom),
				slotMaxTime: formatHour(settings.calendarViewTime?.showCalendarTo),
				initialView: settings.defaultView?.value,
				forceMinimumEventHeight: settings.forceMinimumEventHeight,
				slotDurationDay: settings?.defaultSlotDurationDay?.value,
				slotDurationWeek: settings?.defaultSlotDurationWeek?.value,
				defaultEventLabel: settings?.defaultEventLabel?.value,
			});
		}

		setIsCalendarSettingsLoading(false);
	};

	useEffectAsync(async () => {
		await handleLoadCustomSettings();
	}, []);

	const handleCustomSettingsSave = async () => {
		setIsCalendarSettingOpen(false);
		await handleLoadCustomSettings();
	};

	if (isCalendarSettingsLoading) {
		return <RenderLoading />;
	}

	return (
		<div className={styles.wrapper}>
			<div className={styles.filterWrapper}>
				<AllWorkplacesCalendarFilter
					onFilter={handleOnFilter}
					customButtons={
						<div className={styles.controls}>
							<LinkButton
								title='component.workplaceCalendarToolbar.calendarSettings'
								theme={Theme.BLUE}
								onClick={() => setIsCalendarSettingOpen(true)}
							/>
						</div>
					}
				/>
			</div>
			<div className={styles.calendarWrapper}>
				<Calendar
					events={events}
					onTimeClick={handlePickDate}
					onEventClick={handleEventClick}
					initialView='resourceTimeGridDay'
					onDateChange={handleDateChange}
					allDaySlot={true}
					editable={true}
					onEventDrop={handleEventDrop}
					onEventResize={handleEventDrop}
					resources={resources}
					loadingEvents={loadingEvents}
					headerToolbar={{
						left: 'prev,next,today',
						center: 'title',
						right: 'resourceTimeGridDay,resourceTimeGridWeek',
					}}
					views={{
						day: {
							titleFormat: {
								year: 'numeric',
								month: 'long',
								day: 'numeric',
								weekday: 'long',
							},
						},
					}}
					settings={settings}
				/>
				{selectedEvent && (
					<CalendarContextMenu
						contextParams={contextMenuProps}
						event={selectedEvent}
						onEdit={() => handleEditEvent()}
						onRemove={handleRemoveEvent}
						onCreateNewEvent={handleCreateNewEvent}
						onContextClose={() => {
							setContextMenuProps({ isOpen: false });
						}}
						onMarkEventDone={handleAfterMarkDone}
						removeConfirmationTitle='component.appointment.cancel.title'
						removeConfirmationText='component.appointment.cancel.text'
						onRemoveCancel={() => setSelectedEvent(undefined)}
					/>
				)}
				{detailOpened && (
					<CalendarPatientEventModal
						opened
						patientId={patientId}
						onClose={handleClose}
						selectedDate={selectedDate}
						defaultData={selectedEvent}
						workplaceId={selectedWorkplaceId}
					/>
				)}
				{isCalendarSettingOpen && (
					<CalendarSettingsModal
						isOpen
						userId={userData.id}
						onClose={() => setIsCalendarSettingOpen(false)}
						onSubmit={handleCustomSettingsSave}
						customSettingKey={CustomSettingKey.ALL_WORKPLACES_CALENDAR_SETTINGS}
						allowedFields={{
							showWeekends: true,
							calendarViewTime: true,
							highlightWorkingTime: true,
							defaultView: { enabled: true, options: DEFAULT_VIEW_OPTIONS },
							forceMinimumEventHeight: true,
							defaultSlotDurationDay: {
								enabled: true,
								options: DEFAULT_SLOT_DURATION_OPTIONS,
							},
							defaultSlotDurationWeek: {
								enabled: true,
								options: DEFAULT_SLOT_DURATION_OPTIONS,
							},
							defaultEventLabel: true,
						}}
					/>
				)}
				{isLoading && <OverlayLoading />}
			</div>
		</div>
	);
}
